import React, { useState, useEffect, useRef, useCallback } from 'react';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import inventoryService from '../../services/InventoryService.js';
import { Package, Plus } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { useTranslation } from 'react-i18next';


const InventoryList = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  const categories = inventoryService.getCategories();
  const statuses = inventoryService.getStatuses();

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (categoryFilter) filters.category = categoryFilter;
      if (statusFilter) filters.status = statusFilter;
      if (warehouseFilter) filters.warehouse = warehouseFilter;

      const response = await inventoryService.getAll(currentPage, perPage, filters);
      setItems((prev) => mergeLoadedPage(currentPage, response.data, prev));
      setTotalPages(response.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load inventory items');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, statusFilter, warehouseFilter, currentPage, perPage]);

  usePaginatedListFetch({
    fetchPage: fetchItems,
    setCurrentPage,
    resetPageDeps: [search, categoryFilter, statusFilter, warehouseFilter],
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDelete = async (inventoryId, itemName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      'Delete Inventory Item'
    );
    
    if (confirmed) {
      try {
        await inventoryService.delete(inventoryId);
        Alert.success('Inventory item deleted successfully!');
        
        if (items.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchItems();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete inventory item');
      }
    }
  };

  const toggleDropdown = (inventoryId) => {
    setActiveDropdown(activeDropdown === inventoryId ? null : inventoryId);
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'In Stock': 'bg-green-100 text-green-700',
      'Low Stock': 'bg-yellow-100 text-yellow-700',
      'Out of Stock': 'bg-red-100 text-red-700',
      'On Order': 'bg-blue-100 text-blue-700',
      'Reserved': 'bg-purple-100 text-purple-700',
      'Damaged': 'bg-orange-100 text-orange-700',
      'Expired': 'bg-gray-100 text-gray-700',
      'Discontinued': 'bg-gray-100 text-gray-700',
      'Under Maintenance': 'bg-indigo-100 text-indigo-700'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-700';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value);
  };

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setStatusFilter('');
    setWarehouseFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || categoryFilter || statusFilter || warehouseFilter;

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex h-screen bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <ListPageHeader
                  title="Inventory"
                  icon={Package}
                  titleClassName="text-secondary"
                  iconClassName="w-5 h-5 text-buttonBg shrink-0"
                  className="mb-4 sm:mb-4"
                >
                  <Button
                    mobileIconOnly
                    variant="primary"
                    icon={Plus}
                    onClick={() => navigate('/inventory/add')}
                  >
                    Add Item
                  </Button>
                </ListPageHeader>

                <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-tertiary mb-1 sm:mb-2">{t('common.search')}</label>
                    <SearchSubmitInput
                      value={search}
                      onSearch={setSearch}
                      placeholder="Item name..."
                      inputClassName="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-tertiary mb-1 sm:mb-2">Category</label>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-tertiary"
                    >
                      <option value="">All Categories</option>
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-tertiary mb-1 sm:mb-2">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-tertiary"
                    >
                      <option value="">All Status</option>
                      {statuses.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-tertiary mb-1 sm:mb-2">Warehouse</label>
                    <input
                      type="text"
                      placeholder="Warehouse location..."
                      value={warehouseFilter}
                      onChange={(e) => setWarehouseFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    />
                  </div>
                </ListFilterPanel>

                {/* Table - Desktop View */}
                <div className="bg-white rounded-sm shadow-sm">
                  {loading && currentPage === 1 ? (
                    <div className="p-8 text-center text-gray-600">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                      Loading inventory items...
                    </div>
                  ) : items.length === 0 ? (
                    <div className="p-8 text-center text-gray-600">No inventory items found</div>
                  ) : (
                    <>
                      <div className="hidden md:block w-full">
                        <table className="w-full table-fixed border-collapse">
                          <thead>
                            <tr className="bg-primary text-tertiary">
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">IMAGE</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">ITEM ID</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">ITEM NAME</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">CATEGORY</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">QUANTITY</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">UNIT PRICE</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">TOTAL VALUE</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">STATUS</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">WAREHOUSE</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">ACTIONS</th>
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-200">
                            {items.map((item) => (
                              <tr key={item.inventory_id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 text-center">
                                  <div 
                                    className="w-10 h-10 rounded-md border-2 border-gray-200 flex items-center justify-center bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/inventory/view/${item.inventory_id}`);
                                    }}
                                  >
                                    {item.item_image_base64 ? (
                                      <img
                                        src={`data:image/jpeg;base64,${item.item_image_base64}`}
                                        alt={item.item_name}
                                        className="w-full h-full rounded-md object-cover"
                                      />
                                    ) : (
                                      <span className="text-2xl">📦</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-center font-semibold text-secondary">
                                  <button
                                    onClick={() => navigate(`/inventory/view/${item.inventory_id}`)}
                                    className="text-sm text-secondary hover:underline font-semibold"
                                  >
                                    {item.inventory_id}
                                  </button>
                                </td>
                                <td className="px-4 py-3 text-center text-secondary font-semibold">
                                  <button
                                    onClick={() => navigate(`/inventory/view/${item.inventory_id}`)}
                                    className="font-semibold text-secondary hover:underline truncate max-w-[200px] block"
                                  >
                                    {item.item_name}
                                  </button>
                                </td>
                                <td className="px-4 py-3 text-center text-sm text-secondary font-semibold">{item.category}</td>
                                <td className="px-4 py-3 text-center text-sm text-secondary font-semibold">{item.quantity} {item.unit}</td>
                                <td className="px-4 py-3 text-center text-sm text-secondary font-semibold">{formatCurrency(item.unit_price)}</td>
                                <td className="px-4 py-3 text-center text-sm font-semibold text-secondary">{formatCurrency(item.total_value)}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`px-3 py-1 text-secondary text-sm font-semibold`}>
                                    {item.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center text-sm text-gray-700 truncate max-w-[150px]">{item.warehouse_location}</td>
                                <td className="px-4 py-3 text-center relative">
                                  <button
                                    onClick={() => toggleDropdown(item.inventory_id)}
                                    className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                                    title="Actions"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-5 w-5 text-gray-600"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle cx="12" cy="5" r="2"/>
                                      <circle cx="12" cy="12" r="2"/>
                                      <circle cx="12" cy="19" r="2"/>
                                    </svg>
                                  </button>

                                  {activeDropdown === item.inventory_id && (
                                    <div
                                      ref={dropdownRef}
                                      className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-10"
                                    >
                                      <button
                                        onClick={() => {
                                          navigate(`/inventory/view/${item.inventory_id}`);
                                          setActiveDropdown(null);
                                        }}
                                        className="w-full px-4 py-2 text-center text-sm hover:bg-gray-50 flex gap-2"
                                      >
                                        👁️ View Details
                                      </button>
                                      <button
                                        onClick={() => {
                                          navigate(`/inventory/edit/${item.inventory_id}`);
                                          setActiveDropdown(null);
                                        }}
                                        className="w-full px-4 py-2 text-center text-sm hover:bg-gray-50 flex gap-2"
                                      >
                                        ✏️ Edit
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleDelete(item.inventory_id, item.item_name);
                                          setActiveDropdown(null);
                                        }}
                                        className="w-full px-4 py-2 text-center text-sm text-red-600 hover:bg-red-50 flex gap-2"
                                      >
                                        🗑️ Delete
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        loading={loading && currentPage > 1}
                        onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
                      />
                    </>
                  )}
                </div>

                {/* Card View - Mobile/Tablet */}
                <div className="md:hidden space-y-3 sm:space-y-4">
                  {loading && currentPage === 1 ? (
                    <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                      Loading inventory items...
                    </div>
                  ) : items.length === 0 ? (
                    <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">No inventory items found</div>
                  ) : (
                    <>
                      {items.map((item) => (
                        <div key={item.inventory_id} className="bg-white rounded-lg shadow-sm p-4 sm:p-5 border-l-4 border-l-blue-500">
                          <div className="flex items-start gap-3 sm:gap-4 mb-3">
                            <div 
                              className="w-14 h-14 sm:w-16 sm:h-16 rounded-md border-2 border-gray-200 flex items-center justify-center bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/inventory/view/${item.inventory_id}`);
                              }}
                            >
                              {item.item_image_base64 ? (
                                <img
                                  src={`data:image/jpeg;base64,${item.item_image_base64}`}
                                  alt={item.item_name}
                                  className="w-full h-full rounded-md object-cover"
                                />
                              ) : (
                                <span className="text-4xl">📦</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate">{item.item_name}</h3>
                              <p className="text-xs sm:text-sm text-gray-600">{item.inventory_id}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusBadgeClass(item.status)}`}>
                                {item.status}
                              </span>
                              <RowActionsMenu
                                isOpen={activeDropdown === item.inventory_id}
                                onToggle={() => toggleDropdown(item.inventory_id)}
                                onClose={() => setActiveDropdown(null)}
                                menuRef={dropdownRef}
                                onView={() => navigate(`/inventory/view/${item.inventory_id}`)}
                                onEdit={() => navigate(`/inventory/edit/${item.inventory_id}`)}
                                onDelete={() => handleDelete(item.inventory_id, item.item_name)}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                            <div>
                              <span className="text-gray-500">Category:</span>
                              <p className="text-gray-900 font-medium">{item.category}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Quantity:</span>
                              <p className="text-gray-900 font-medium">{item.quantity} {item.unit}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Unit Price:</span>
                              <p className="text-gray-900 font-medium">{formatCurrency(item.unit_price)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Total Value:</span>
                              <p className="text-gray-900 font-semibold">{formatCurrency(item.total_value)}</p>
                            </div>
                          </div>
                        </div>
                      ))}

                    </>
                  )}
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryList;